<template>
    <b-modal
      id="add-or-update"
      :visible="isAddOrUpdateActive"
      centered
      hide-footer
      hide-header
      no-close-on-esc
      no-close-on-backdrop
      @hidden="resetForm" 
      @hide="resetForm"
      @change="(val) => $emit('update:is-add-or-update-active', val)"
    >
      <template #default="{ hide }">
        <b-overlay :show="loading" class="px-2">
          <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
            <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
              {{ isUpdate ? 'Update Department' : 'Add New Department' }}
            </h5>
  
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
  
          <validation-observer
            #default="{ handleSubmit }"
            ref="update_modal_form"
          >
            <b-form
              class="my-1"
              @submit.prevent="handleSubmit(onCreateOrUpdate)"
              @reset.prevent="resetForm"
            >
  
              <b-alert
                v-if="errorMessage"
                variant="danger"
                :show="true"
              >
                <div class="alert-body font-small-2">
                  <b-card-text class="mb-0">
                    {{ errorMessage }}
                  </b-card-text>
                </div>
              </b-alert>
  
        
              
              <validation-provider
                #default="validationContext"
                name="Title"
                rules="required"
              >
                <b-form-group
                  label="Title"
                  label-for="title"
                >
                  <template #label>
                    <span>Title<sup style="color: tomato">*</sup></span>
                  </template>
  
                  <b-form-input
                    id="title"
                    v-model="new_data.title"
                    autofocus
                    placeholder=""
                    :disabled="disableForm"
                    :state="getValidationState(validationContext)"
                  />
  
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
  
  
              <!-- Description -->
              <validation-provider
                #default="validationContext"
                name="Description"
                rules=""
              >
                <b-form-group
                  label="Description"
                  label-for="description"
                >
                  <b-form-textarea
                    id="description"
                    v-model="new_data.description"
                    :state="getValidationState(validationContext)"
                    :disabled="disableForm"
                  />
  
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
  
              <!-- Form Actions -->
              <div class="d-flex mt-2">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-2"
                  type="submit"
                  :disabled="disableForm"
                >
                  {{ isUpdate ? 'Update' : 'Add' }}
                </b-button>
  
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="button"
                  variant="outline-secondary"
                  @click="hide"
                >
                  Cancel
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-overlay>
      </template>
    </b-modal>
  </template>
  
  <script>
  import {
    BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
    BFormTextarea, BFormInvalidFeedback, BButton, BFormFile, BFormCheckbox,
    BAlert, BCardText, BModal, BMedia, BMediaBody, BMediaAside, BImg,
  } from 'bootstrap-vue'
  
  import { required } from '@validations'
  import { get, cloneDeep, omit } from 'lodash';
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  
  import vSelect from 'vue-select'
  import Ripple from 'vue-ripple-directive'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
  
  const rawMemberTypesData = {
    title: "",
    description: "",
  }
  
  export default {
    components: {
      BImg,
      BForm,
      BAlert,
      BModal,
      BMedia, 
      BButton,
      vSelect,
      BOverlay,
      BSidebar,
      BCardText,
      BFormFile,
      BFormGroup,
      BMediaBody, 
      BFormInput,
      BMediaAside,
      BFormCheckbox,
      BFormTextarea,
      BFormInvalidFeedback,
  
      // Form Validation
      ValidationProvider,
      ValidationObserver,
    },
    directives: {
      Ripple,
    },
    model: {
      prop: 'isAddOrUpdateActive',
      event: 'update:is-add-or-update-active',
    },
    props: {
      isAddOrUpdateActive: {
        type: Boolean,
        required: true,
      },
      updateData: {
        type: Object,
        default: null,
        required: false
      },

    },
    data() {
      return {
        new_data: cloneDeep(rawMemberTypesData),
        loading: false,
        errorMessage: "",
        required,
      }
    },
    computed: {
      isUpdate(){
        return !!this.updateData
      },
      disableForm(){
        if (this.isUpdate){
          return !this.can("departments:CanUpdate")
        }
  
        return !this.can("departments:CanAddNew")
      },
    },
    watch: {
      updateData: {
        handler(updateData){
          if (updateData){
            this.new_data = {
              _id: updateData._id,
              title: updateData.title,
              description: updateData.description,
            }
          }
        },
        immediate: true,
        deep: true
      },
    },
    methods: {
      async onCreateOrUpdate(){
        try {
          this.loading = true;
          this.errorMessage = "";
  
          const success = await this.$refs.update_modal_form.validate();
          if (!success){
            return;
          }
  
          const messages = {
            update: {
              title: "Confirm Department Update",
              text: "You are about to update details of a department.",
              icon: "warning"
            },
            new: {
              title: "Create Department",
              text: "You are about to create a department",
              icon: "info"
            }
          }
  
          /* eslint-disable no-nested-ternary */
          const message = this.isUpdate ? messages.update : messages.new;
          const { title, text, icon } = message;
  
          const result = await this.$swal({
            title,
            text,
            icon,
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });
          if (!result.isConfirmed) {
            return;
          }
  
          if (this.isUpdate){
            const id = this.new_data._id
            const update_data = omit(this.new_data, "_id");
  
            if (!this.can("departments:CanUpdate")) {
              return
            }
  
            await this.useJwt().mainService.departments.update(id, update_data);
          } else {
            if (!this.can("departments:CanAddNew")) {
              return
            }

            await this.useJwt().mainService.departments.create(this.new_data);
          }
          this.$emit("refetchData");
          this.$emit('update:is-add-or-update-active', false);
  
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success!',
              icon: 'AlertQuestionIcon',
              variant: 'success',
              text: `Member ${this.isUpdate ? 'updated' : 'added'} successfully`
            },
          });
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          this.errorMessage = error_message;
  
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.loading = false
        }
      },
  
      resetForm() {
        this.$emit("resetUpdateData")
        this.new_data = {};
      }
    }
  }
  </script>
  
  <style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
  
  #add-or-update {
    .vs__dropdown-menu {
      max-height: 200px !important;
    }
  }
  </style>
